import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Flag, Moon, Sun, ExternalLink } from "lucide-react";
import { NAV, LEGENDTEAM_URL, LOGO_URL, logoCardCls } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";
import RadioPlayer from "@/components/RadioPlayer";

const LOGO = LOGO_URL;

const MiniLogo = ({ name, logo, url, testid }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    title={name}
    className={`flex items-center justify-center rounded-sm h-16 px-2 hover:border-legend-red hover:shadow-md transition-all ${logoCardCls(name)}`}
  >
    <img src={logo} alt={name} className="max-h-12 max-w-[130px] object-contain" />
  </a>
);

const LEFT = [
  { name: "Simoni Racing", logo: "/sponsors/simoniracing.png", url: "https://simoniracing.com", testid: "masthead-sponsor-simoni" },
  { name: "Essex Racing Oils & Fuels", logo: "/sponsors/essex.png", url: "https://essexlubra.com", testid: "masthead-sponsor-essex" },
  { name: "La Pieda", logo: "/sponsors/lapieda.png", url: "https://www.facebook.com/Ristorantelapieda/?locale=it_IT", testid: "masthead-sponsor-lapieda" },
  { name: "Netso S.p.A.", logo: "/sponsors/netso.png", url: "https://netso.it", testid: "masthead-sponsor-netso" },
  { name: "Levii Trentodoc", logo: "/sponsors/levii.png", url: "https://levii.it", testid: "masthead-sponsor-levii" },
  { name: "Pandora Consulting", logo: "/sponsors/pandora-consulting.png", url: "https://pandora-consulting.it", testid: "masthead-sponsor-pandora" },
  { name: "Sassa Motor & Sport", logo: "/sponsors/sassa-motorsport.png", url: "https://www.sassamotorsport.com", testid: "masthead-sponsor-sassa" },
  { name: "Telefonia Vilar", logo: "/sponsors/telefonia-vilar.jpg", url: "https://www.facebook.com/vilar.telefonia/", testid: "masthead-sponsor-vilar" },
];
const RIGHT = [
  { name: "MCG S.r.l. — Mulinari Costruzioni Generali", logo: "/sponsors/mcg-v2.png", url: "https://mulinaricostruzioni.it", testid: "masthead-sponsor-mulinari" },
  { name: "Merlin Solution", logo: "/sponsors/merlin.png", url: "https://merlinsolution.eu", testid: "masthead-sponsor-merlin" },
  { name: "Kemipol", logo: "/sponsors/kemipol.png", url: "https://kemipolsrl.it", testid: "masthead-sponsor-kemipol" },
  { name: "Thinnk", logo: "/sponsors/thinnk.png", url: "http://www.thinnk.it", testid: "masthead-sponsor-thinnk" },
  { name: "Medius S.r.l. Edilizia", logo: "/sponsors/medius.png", url: "https://atoka.io/public/it/azienda/medius-srl/c8059f39c8d7", testid: "masthead-sponsor-medius" },
  { name: "AGM Automazioni", logo: "/sponsors/agm-automazioni.jpg", url: "https://www.agmautomazioni.it", testid: "masthead-sponsor-agm" },
];


export default function Masthead({ sponsors = [] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const { lang, setLang, t } = useLang();

  const today = new Date().toLocaleDateString(t("dateLocale"), {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("legend_theme", next ? "dark" : "light");
  };

  return (
    <header className="w-full bg-white dark:bg-legend-ink border-b-2 border-legend-red">
      {/* Top strip */}
      <div className="bg-legend-ink text-white text-[11px] md:text-xs font-mono">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between gap-2">
          <span data-testid="masthead-date" className="capitalize truncate">{today}</span>
          <span className="hidden sm:inline uppercase tracking-widest text-slate-300">
            {t("periodicity")} <span className="text-legend-yellow font-semibold">{t("monthly")}</span> · {t("reg")}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 font-semibold" data-testid="lang-toggle">
              <button onClick={() => setLang("it")} data-testid="lang-it" className={`flex items-center gap-1 ${lang === "it" ? "text-legend-yellow" : "text-slate-400 hover:text-white"}`}><span aria-hidden>🇮🇹</span> IT</button>
              <span className="text-slate-600">|</span>
              <button onClick={() => setLang("en")} data-testid="lang-en" className={`flex items-center gap-1 ${lang === "en" ? "text-legend-yellow" : "text-slate-400 hover:text-white"}`}><span aria-hidden>🇬🇧</span> EN</button>
            </div>
            <button onClick={toggleDark} data-testid="theme-toggle" aria-label="Cambia tema" className="hover:text-legend-yellow transition-colors">
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </div>
      </div>

      {/* Masthead */}
      <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-4">
        <div className="hidden md:block justify-self-start">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Pubblicità</span>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {LEFT.map((s) => <MiniLogo key={s.testid} {...s} />)}
          </div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <Link to="/" data-testid="masthead-logo" className="flex flex-col items-center">
            <img src={LOGO} alt="Legend Magazine" className="h-24 sm:h-32 md:h-40 max-w-full object-contain" />
            <div className="checkered h-1.5 w-64 sm:w-[28rem] mt-2 opacity-80" />
          </Link>
          <RadioPlayer horizontal />
        </div>
        <div className="hidden md:flex items-center gap-3 justify-self-end">
          <div className="text-right">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Pubblicità</span>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {RIGHT.map((s) => <MiniLogo key={s.testid} {...s} />)}
              <a
                href="https://www.instagram.com/copparomagnaslalomofficial/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="masthead-partner-coppa-romagna"
                title="Coppa Romagna Slalom — Instagram"
                className="flex items-center justify-center rounded-sm h-16 px-2 border border-slate-200 dark:border-white/15 bg-white hover:border-legend-red hover:shadow-md transition-all"
              >
                <img src="/sponsors/copparomagna.png" alt="Coppa Romagna Slalom" className="max-h-12 max-w-[130px] object-contain" />
              </a>
            </div>
            <a
              href="https://www.instagram.com/espgroup2025"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="masthead-partner-esp"
              title="Ecologisti Sentieri Puliti — Instagram"
              className="mt-2 flex items-center justify-center gap-2 rounded-sm border border-slate-200 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1.5 hover:border-legend-red hover:shadow-sm transition-all"
            >
              <span className="font-display font-black text-lg leading-none tracking-tight">
                <span className="text-[#009246]">E.</span><span className="text-slate-900 dark:text-white">S.</span><span className="text-[#ce2b37]">P.</span>
              </span>
              <span className="text-left text-[8px] font-mono uppercase tracking-widest text-slate-500 dark:text-slate-400 leading-tight">Ecologisti<br />Sentieri Puliti</span>
            </a>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-legend-ink text-white sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="hidden md:flex items-center">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="px-4 py-3 text-sm font-semibold uppercase tracking-wide hover:text-legend-yellow hover:bg-white/5 transition-colors"
              >
                {lang === "en" ? n.en : n.label}
              </Link>
            ))}
          </div>
          <button className="md:hidden py-3 flex items-center gap-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={20} /> : <Menu size={20} />}
            <span className="font-display uppercase font-bold">Menu</span>
          </button>
          <a
            href={LEGENDTEAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="legendteam-nav-btn"
            className="my-2 inline-flex items-center gap-1.5 bg-legend-red hover:bg-legend-dark text-white text-sm font-bold uppercase tracking-wide px-4 py-2 rounded-sm transition-colors"
          >
            <Flag size={14} /> LegendTeam.it <ExternalLink size={12} />
          </a>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 pb-2">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => { navigate(n.to); setOpen(false); }}
                className="block w-full text-left px-4 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-white/5"
              >
                {lang === "en" ? n.en : n.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
}
